"use client"

import { useState } from "react"
import ClipLoader from "react-spinners/ClipLoader"
import { type WorkBook } from "xlsx"
import { FileField } from "../../projects/components/CreateProjectForm/FileField"
import { useColumnTypes } from "../../projects/hooks/useColumnTypes"
import { useDatasetMetadata } from "../../projects/hooks/useDatasetMetadata"
import { useWorkbook } from "../../projects/hooks/useWorkbook"
import type { DataColumn, DataColumnWithType } from "../../projects/types"

export function Calculator() {
    const [file, setFile] = useState<File | null>(null)
    const [workbook, setWorkbook] = useState<WorkBook>()
    const [sheetNames, setSheetNames] = useState<string[]>([])
    const [selectedSheet, setSelectedSheet] = useState<string>("")
    const [allColumns, setAllColumns] = useState<DataColumn[]>([])
    const [dimensions, setDimensions] = useState<{ rows: number; columns: number }>()
    const [columnsWithType, setColumnsWithType] = useState<DataColumnWithType[]>([])

    useWorkbook({
        file,
        setWorkbook,
        setSheetNames,
        setSelectedSheet,
    })

    useDatasetMetadata({
        workbook,
        selectedSheet,
        setAllColumns,
        setDimensions,
    })

    const { loading } = useColumnTypes({
        allColumns,
        dimensions,
        setColumnsWithType,
    })

    const upload = (files: FileList) => {
        if (files) {
            setFile(files[0])
        }
    }

    const reset = () => {
        setFile(null)
        setSheetNames([])
        setSelectedSheet("")
        setAllColumns([])
        setDimensions(undefined)
        setColumnsWithType([])
    }

    if (file == null) return <FileField upload={upload} />

    return (
        <>
            {sheetNames.length > 1 && (
                <div className="col-span-full">
                    <label htmlFor="sheet" className="block font-semibold leading-6 text-gray-900">
                        Sheet
                    </label>
                    <div className="mt-2">
                        <select
                            id="sheet"
                            name="sheet"
                            className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary-600 focus:outline-none focus:ring-primary-600 sm:text-sm"
                            value={selectedSheet}
                            onChange={(e) => setSelectedSheet(e.target.value)}
                        >
                            <option className="italic" value="">
                                Select a sheet
                            </option>
                            {sheetNames.map((sheetName) => (
                                <option key={sheetName} value={sheetName}>
                                    {sheetName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
            {loading && columnsWithType.length === 0 && (
                <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader size={40} color="#088d96" />
                </div>
            )}

            {columnsWithType.length > 0 && (
                <>
                    <table className="mt-10 min-w-full divide-y divide-gray-300">
                        <thead>
                            <tr>
                                <th
                                    scope="col"
                                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                                >
                                    Data type
                                </th>
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Quantity
                                </th>
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Cost
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            <tr>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                                    Text
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                                    {columnsWithType
                                        .filter((c) => c.type === "text")
                                        .reduce((acc, c) => acc + c.quantity, 0)
                                        .toLocaleString()}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                                    {columnsWithType
                                        .filter((c) => c.type === "text")
                                        .reduce((acc, c) => acc + c.cost, 0)
                                        .toLocaleString()}{" "}
                                    credits
                                </td>
                            </tr>
                            <tr>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                                    List
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                                    {columnsWithType
                                        .filter((c) => c.type === "list")
                                        .reduce((acc, c) => acc + c.quantity, 0)
                                        .toLocaleString()}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                                    {columnsWithType
                                        .filter((c) => c.type === "list")
                                        .reduce((acc, c) => acc + c.cost, 0)
                                        .toLocaleString()}{" "}
                                    credits
                                </td>
                            </tr>
                            <tr>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                                    Unique Identifier
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                                    {columnsWithType
                                        .filter((c) => c.type === "id")
                                        .reduce((acc, c) => acc + c.quantity, 0)
                                        .toLocaleString()}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">Free</td>
                            </tr>
                            <tr>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                                    Summary report
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">1</td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">Free</td>
                            </tr>
                        </tbody>
                    </table>
                </>
            )}
        </>
    )
}
